import { memo } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Flex,
    Button,
    Image,
    useDisclosure,
    useColorModeValue,
    VStack,
    HStack,
    IconButton,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Link as ChakraLink,
    useBreakpointValue,
    Icon,
} from '@chakra-ui/react';

// Utils
import { handleNavigation } from '../../utils/navigation';

// Hooks
import { useScroll } from '../../hooks/useScroll';

// Constants
import { UI_CONFIG } from '../../constants';

// Options
import { defaultNavigationOptions } from './options';

// Components
import Logo from '../ui/Logo';
import LanguageSelector from '../ui/LanguageSelector';

// Hooks
import { useTranslation } from '../../hooks/useTranslation';

const Navbar = memo(() => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isScrolled } = useScroll(UI_CONFIG.SCROLL_THRESHOLD);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // Colors
    const bgColor = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('brand.text', 'white');
    const textSecondaryColor = useColorModeValue('brand.textSecondary', 'gray.300');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

    // Responsive values
    const navHeight = useBreakpointValue({
        base: isScrolled ? '60px' : '70px',
        md: isScrolled ? '70px' : '80px',
    });

    const fontSize = useBreakpointValue({
        base: '12px',
        md: '13px',
        lg: '14px',
    });

    const mobileButton = (icon) => {
        return (
            <Button
                size={'md'}
                backgroundColor={'transparent'}
                onClick={isOpen ? onClose : onOpen}
                _hover={{ bg: hoverBgColor, color: 'brand.main' }}
                _active={{ bg: hoverBgColor }}
                borderRadius="md"
                p={2}
                color={textSecondaryColor}
            >
                {icon}
            </Button>
        );
    };

    const isActivePage = (link) => {
        return location.pathname === link;
    };

    return (
        <>
            <Box
                position="sticky"
                top="0"
                w="100%"
                bg={bgColor}
                boxShadow={isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : '0 1px 0 rgba(0, 0, 0, 0.05)'}
                transition="all 0.3s ease"
                zIndex={1000}
                borderBottom={`1px solid ${borderColor}`}
                _before={{
                    content: '""',
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    height: '2px',
                    bg: 'brand.light',
                    opacity: 0.6,
                }}
            >
                <Flex
                    h={navHeight}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    px={{ base: 4, md: 6, lg: 8 }}
                    maxW="1400px"
                    mx="auto"
                    transition="all 0.3s ease"
                >
                    {/* Logo */}
                    <Logo
                        size={isScrolled ? 'small' : 'default'}
                        color={textColor}
                        showHover={true}
                    />

                    {/* Desktop Navigation */}
                    <HStack
                        spacing={5}
                        display={{ base: 'none', md: 'flex' }}
                        textTransform="uppercase"
                        color={textSecondaryColor}
                        fontSize={fontSize}
                        fontWeight="medium"
                        letterSpacing="wide"
                    >
                        {defaultNavigationOptions.map((option, index) => (
                            <ChakraLink
                                key={index}
                                color={isActivePage(option.link) ? 'brand.main' : textSecondaryColor}
                                _hover={{
                                    color: 'brand.main',
                                    textDecoration: 'none',
                                    _after: {
                                        width: '20px',
                                    },
                                }}
                                transition="all 0.3s ease"
                                cursor="pointer"
                                position="relative"
                                onClick={(e) => handleNavigation(option.link, e, navigate)}
                                _after={{
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-8px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: isActivePage(option.link) ? '20px' : '0px',
                                    height: '2px',
                                    bg: 'brand.main',
                                    transition: 'width 0.3s ease',
                                    borderRadius: '1px',
                                }}
                            >
                                {t(`navigation.${option.key}`)}
                            </ChakraLink>
                        ))}
                        <LanguageSelector />
                    </HStack>

                    {/* Mobile Menu Button */}
                    <Box display={{ base: 'block', md: 'none' }}>
                        {isOpen ? (
                            mobileButton(
                                <Icon viewBox="0 0 24 24" boxSize={5}>
                                    <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </Icon>,
                            )
                        ) : (
                            mobileButton(
                                <Icon viewBox="0 0 24 24" boxSize={5}>
                                    <path fill="currentColor" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                                </Icon>,
                            )
                        )}
                    </Box>
                </Flex>

                {/* Mobile Navigation */}
                {isOpen && (
                    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                        <DrawerOverlay />
                        <DrawerContent>
                            <DrawerCloseButton />
                            <DrawerHeader>
                                <Logo
                                    size="small"
                                    color={textColor}
                                    showHover={true}
                                />
                            </DrawerHeader>
                            <DrawerBody>
                                <VStack spacing={2}>
                                    {defaultNavigationOptions.map((option, index) => (
                                        <ChakraLink
                                            key={index}
                                            onClick={(e) => {
                                                handleNavigation(option.link, e, navigate);
                                                onClose();
                                            }}
                                            color={isActivePage(option.link) ? 'brand.main' : textSecondaryColor}
                                            _hover={{
                                                color: 'brand.main',
                                                textDecoration: 'none',
                                                bg: hoverBgColor,
                                                transform: 'translateX(8px)',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                            transition="all 0.3s ease"
                                            cursor="pointer"
                                            w="100%"
                                            py={4}
                                            px={6}
                                            borderRadius="lg"
                                            textAlign="center"
                                            borderLeft={isActivePage(option.link) ? '4px solid' : '4px solid transparent'}
                                            borderColor={isActivePage(option.link) ? 'brand.main' : 'transparent'}
                                            bg={isActivePage(option.link) ? hoverBgColor : 'transparent'}
                                            fontWeight={isActivePage(option.link) ? 'semibold' : 'medium'}
                                            position="relative"
                                            _before={{
                                                content: '""',
                                                position: 'absolute',
                                                left: '0',
                                                top: '0',
                                                bottom: '0',
                                                width: isActivePage(option.link) ? '4px' : '0px',
                                                bg: 'brand.main',
                                                borderRadius: '0 2px 2px 0',
                                                transition: 'width 0.3s ease',
                                            }}
                                        >
                                            {t(`navigation.${option.key}`)}
                                        </ChakraLink>
                                    ))}

                                    {/* Language Selector in Mobile Menu */}
                                    <Box pt={4}>
                                        <LanguageSelector />
                                    </Box>
                                </VStack>
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                )}
            </Box>
        </>
    );
});

Navbar.displayName = 'Navbar';

export default Navbar;
